import { useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/api/client";
import { queryKeys } from "@/lib/queryKeys";
import { useProjects } from "@/hooks/useProjects";
import { useProjectDetail } from "@/hooks/useProjectDetail";

export interface Provenance {
  repo_url: string;
  branch: string;
  commit_sha: string;
  author: string | null;
}

export interface GovernanceArtifactItem {
  id: string;
  type: string;
  path: string;
  content_hash: string;
  content_summary: string | null;
  scope: string;
  project_id: string | null;
  provenance: Provenance;
  is_deleted: boolean;
  synced_at: string;
  created_at: string;
  updated_at: string;
}

interface GovernanceArtifactPullResponse {
  artifacts: GovernanceArtifactItem[];
  count: number;
  total: number;
  server_time: string;
}

/**
 * Three-hop resolution: slug -> projectId -> repositories -> artifacts (SD-4).
 * Enabled only when the primary repository's ID has resolved.
 */
export function useGovernanceArtifacts(projectSlug: string | null) {
  const {
    projects,
    loading: projectsLoading,
    error: projectsError,
  } = useProjects();
  const projectId = projectSlug
    ? (projects.find((p) => p.slug === projectSlug)?.id ?? null)
    : null;

  const { repositories, loading: reposLoading, error: reposError } =
    useProjectDetail(projectId);

  const repoId =
    repositories.find((r) => r.is_primary)?.repository_id ?? null;

  const query = useQuery({
    queryKey: queryKeys.governance.artifacts(repoId ?? "", {
      limit: 500,
      offset: 0,
    }),
    queryFn: () =>
      api<GovernanceArtifactPullResponse>(
        `/api/v2/governance/artifacts?repo_id=${repoId}&limit=500`,
      ),
    enabled: repoId !== null,
  });

  const refetch = useCallback(() => {
    void query.refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.refetch]);

  return {
    artifacts: query.data?.artifacts ?? [],
    loading:
      projectSlug !== null &&
      (projectsLoading || reposLoading || query.isLoading),
    error:
      projectsError ||
      reposError ||
      (query.error ? "Failed to load governance artifacts" : ""),
    refetch,
    repoId,
  };
}
